// MCHD Room Scheduler - User Management Hook
// Custom hook for admin user management operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { 
  registerUser, 
  RegisterRequest, 
  User, 
  ApiResponse 
} from '../../api/endpoints/auth';
import { getUsers } from '../../api/endpoints/users';

// Real API function for getting users
const getUsersData = async (token: string, params: { page?: number; limit?: number; search?: string; role?: string } = {}): Promise<ApiResponse<{ users: User[]; pagination: any }>> => {
  return await getUsers(params, token);
};

// Mock function for updating users - will be replaced with actual API
const updateUser = async (
  id: string, 
  userData: Partial<RegisterRequest>, 
  _token: string
): Promise<ApiResponse<User>> => {
  // TODO: Implement actual API call when backend endpoint is ready
  return {
    success: true,
    data: {
      id,
      email: userData.email || 'updated@moh.gov.sa',
      name: userData.name || 'Updated User',
      role: userData.role || 'DOCTOR',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
};

// Mock function for deleting users - will be replaced with actual API
const deleteUser = async (_id: string, _token: string): Promise<ApiResponse> => {
  // TODO: Implement actual API call when backend endpoint is ready
  return {
    success: true,
    data: { message: 'User deleted successfully' },
  };
};

/**
 * Custom hook for managing user operations
 * @param token - JWT token for authentication
 * @returns User management state and functions
 */
export function useUserManagement(token: string) {
  const queryClient = useQueryClient();

  // Get users query
  const { 
    data: usersData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsersData(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (userData: RegisterRequest) => registerUser(userData),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        notifications.show({
          title: 'Success',
          message: 'User created successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to create user',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create user',
        color: 'red',
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: Partial<RegisterRequest> }) => 
      updateUser(id, userData, token),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        notifications.show({
          title: 'Success',
          message: 'User updated successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to update user',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update user',
        color: 'red',
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id, token),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        notifications.show({
          title: 'Success',
          message: 'User deleted successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to delete user',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete user',
        color: 'red',
      });
    },
  });

  return {
    // Data
    users: usersData?.data?.users || [],
    isLoading,
    error: error?.message || null,
    
    // Actions
    createUser: createUserMutation.mutate,
    updateUser: updateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
    
    // Loading states
    isCreating: createUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    
    // Refetch function
    refetch: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  };
}
